import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../../../shared/api/client';

/**
 * Fluxo "Informar pagamento da NF" (tela `ColaboradorPagamentoNF` do GeneXus) —
 * card [PiEuG4Wx]/ATD-0059 (GATE_OK; smoke verde). Marca a NF como paga e,
 * opcionalmente, desconta parcelas de vale pendentes do colaborador (vincula
 * cada parcela à NF). Sem parcela = conferência (só marca `nfPaga`).
 *
 * Endpoints: `GET /employees/:id/vales/parcelas-pendentes` +
 * `POST /employees/:id/notas-fiscais/:nfId/pagamento`.
 */
export interface ValeParcelaPendente {
  valeCodigo: number;
  /** `id` = ValeParcelaCodigo (sequencial dentro do vale). */
  id: number;
  dataVencimento: string;
  valor: number;
  status: string;
}

export interface InformarPagamentoInput {
  parcelas: Array<{ valeCodigo: number; parcelaCodigo: number }>;
}

async function listParcelasPendentes(employeeId: number): Promise<ValeParcelaPendente[]> {
  const { data } = await api.get<{ data: ValeParcelaPendente[] }>(
    `/v1/employees/${employeeId}/vales/parcelas-pendentes`,
    { params: { pagina: 1, porPagina: 100 } },
  );
  return data.data;
}

async function informarPagamento(
  employeeId: number,
  notaFiscalId: number,
  input: InformarPagamentoInput,
): Promise<void> {
  await api.post(`/v1/employees/${employeeId}/notas-fiscais/${notaFiscalId}/pagamento`, input);
}

export function useParcelasPendentes(
  employeeId: number,
  enabled: boolean,
): UseQueryResult<ValeParcelaPendente[], Error> {
  return useQuery({
    queryKey: ['employees', employeeId, 'vales', 'parcelas-pendentes'],
    queryFn: () => listParcelasPendentes(employeeId),
    enabled,
    staleTime: 15_000,
  });
}

export function useInformarPagamento(
  employeeId: number,
  notaFiscalId: number,
): UseMutationResult<void, Error, InformarPagamentoInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => informarPagamento(employeeId, notaFiscalId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees', employeeId, 'notas-fiscais'] });
      qc.invalidateQueries({ queryKey: ['notas-fiscais', 'global'] });
      qc.invalidateQueries({ queryKey: ['employees', employeeId, 'vales'] });
    },
  });
}
